import React, { useState, useEffect } from 'react';
import { 
  FaWallet, FaCalendarAlt, FaExclamationCircle, 
  FaSlidersH, FaLock, FaThLarge, FaChartBar, FaFileInvoiceDollar, FaCoins, FaUser, FaCreditCard
} from 'react-icons/fa';

import BillTrackerAuth from '../components/billtracker/BillTrackerAuth';
import DashboardSheet from '../components/billtracker/DashboardSheet';
import ExpensesSheet from '../components/billtracker/ExpensesSheet';
import IncomeSheet from '../components/billtracker/IncomeSheet';
import AnalyticsSheet from '../components/billtracker/AnalyticsSheet';
import CreditCardTracker from '../components/billtracker/CreditCardTracker';
import TuesdayInstallmentModal from '../components/billtracker/TuesdayInstallmentModal';
import ParametersModal from '../components/billtracker/ParametersModal';

import { 
  DEFAULT_EXPENSES, 
  DEFAULT_INCOME_STREAMS, 
  DEFAULT_CREDIT_CARD,
  DEFAULT_CONFIG, 
  SESSION_KEY, 
  CURRENT_USER_KEY,
  loadUserLedger,
  saveUserLedger,
  syncRecurringExpenses,
  sortBillsByDueDate,
  getTodayDateStr,
  getYearMonth,
  syncIncomeStreamsForMonth
} from '../components/billtracker/billTrackerData';

import '../components/billtracker/BillTracker.css';

const BillTrackerPage = () => {
  // Authentication State
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem(SESSION_KEY) === 'true';
  });

  // Current logged in username
  const [currentUser, setCurrentUser] = useState(() => {
    return localStorage.getItem(CURRENT_USER_KEY) || 'adedeji';
  });

  // Active Sheet Tab: 'dashboard' | 'analytics' | 'expenses' | 'income' | 'credit-card'
  const [activeTab, setActiveTab] = useState('dashboard');

  // Ledger state loaded strictly isolated for the active user, arranged by due date
  const [expenses, setExpenses] = useState(() => {
    const user = localStorage.getItem(CURRENT_USER_KEY) || 'adedeji';
    const ledger = loadUserLedger(user);
    return sortBillsByDueDate(ledger.expenses || []);
  });

  const [incomeStreams, setIncomeStreams] = useState(() => {
    const user = localStorage.getItem(CURRENT_USER_KEY) || 'adedeji';
    const ledger = loadUserLedger(user);
    return ledger.incomeStreams || [];
  });

  const [creditCard, setCreditCard] = useState(() => {
    const user = localStorage.getItem(CURRENT_USER_KEY) || 'adedeji';
    const ledger = loadUserLedger(user);
    return ledger.creditCard || DEFAULT_CREDIT_CARD;
  });

  const [config, setConfig] = useState(() => {
    const user = localStorage.getItem(CURRENT_USER_KEY) || 'adedeji';
    const ledger = loadUserLedger(user);
    return ledger.config || DEFAULT_CONFIG;
  });

  // Theme: 'light' or 'dark'
  const [theme, setTheme] = useState('light');

  // Modal states
  const [selectedTuesdayBill, setSelectedTuesdayBill] = useState(null);
  const [isParametersOpen, setIsParametersOpen] = useState(false);

  // Sync active user's data to user-specific localStorage key
  useEffect(() => {
    if (currentUser) {
      saveUserLedger(currentUser, { expenses, incomeStreams, creditCard, config });
    }
  }, [currentUser, expenses, incomeStreams, creditCard, config]);

  // Keep referenceDate automatically synchronized with real calendar day
  useEffect(() => {
    const today = getTodayDateStr();
    if (config?.autoSyncRealDate !== false && config?.referenceDate !== today) {
      setConfig(prev => ({ ...prev, referenceDate: today, autoSyncRealDate: true }));
    }
  }, [config?.autoSyncRealDate, config?.referenceDate]);

  // Auto-sync recurring expenses when reference date advances to following month(s)
  useEffect(() => {
    if (expenses && expenses.length > 0 && config && config.referenceDate) {
      const synced = syncRecurringExpenses(expenses, config.referenceDate);
      if (synced !== expenses && synced.length !== expenses.length) {
        setExpenses(synced);
      }
    }
  }, [config, expenses]);

  // Synchronize income streams to active reference month whenever referenceDate changes
  useEffect(() => {
    if (incomeStreams && incomeStreams.length > 0 && config && config.referenceDate) {
      const activeYM = getYearMonth(config.referenceDate);
      const synced = syncIncomeStreamsForMonth(incomeStreams, activeYM);
      const hasDiff = synced.some((s, idx) => 
        s.actualReceived !== incomeStreams[idx]?.actualReceived ||
        s.expectedAmount !== incomeStreams[idx]?.expectedAmount
      );
      if (hasDiff) {
        setIncomeStreams(synced);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.referenceDate]);

  // Handle successful login or account creation
  const handleAuthenticated = (username) => {
    const cleanUser = (username || 'adedeji').trim().toLowerCase();
    setCurrentUser(cleanUser);
    const ledger = loadUserLedger(cleanUser);
    setExpenses(sortBillsByDueDate(ledger.expenses || []));
    setIncomeStreams(ledger.incomeStreams || []);
    setCreditCard(ledger.creditCard || DEFAULT_CREDIT_CARD);
    if (ledger.config) setConfig(ledger.config);
    setIsAuthenticated(true);
  };

  // Handle Logout / Lock
  const handleLock = () => {
    localStorage.removeItem(SESSION_KEY);
    setIsAuthenticated(false);
  };

  // Toggle single installment payment
  const handleToggleInstallmentPaid = (billId, installmentIndex) => {
    setExpenses(prev => prev.map(bill => {
      if (bill.id === billId && bill.installments) {
        const updatedInstallments = [...bill.installments];
        updatedInstallments[installmentIndex] = {
          ...updatedInstallments[installmentIndex],
          isPaid: !updatedInstallments[installmentIndex].isPaid
        };
        // If all installments are paid, mark whole bill as paid
        const allPaid = updatedInstallments.every(inst => inst.isPaid);
        return {
          ...bill,
          installments: updatedInstallments,
          isPaid: allPaid
        };
      }
      return bill;
    }));

    // also update selectedTuesdayBill in modal
    if (selectedTuesdayBill && selectedTuesdayBill.id === billId) {
      setSelectedTuesdayBill(prev => {
        const updatedInstallments = [...prev.installments];
        updatedInstallments[installmentIndex] = {
          ...updatedInstallments[installmentIndex],
          isPaid: !updatedInstallments[installmentIndex].isPaid
        };
        const allPaid = updatedInstallments.every(inst => inst.isPaid);
        return {
          ...prev,
          installments: updatedInstallments,
          isPaid: allPaid
        };
      });
    }
  };

  // Calculate Overdue sum for header pill
  const refDateObj = new Date(config.referenceDate);
  const overdueSum = expenses
    .filter(b => !b.isPaid && new Date(b.dueDate) < refDateObj)
    .reduce((sum, b) => sum + b.amount, 0);

  // If user is not authenticated, render login guard
  if (!isAuthenticated) {
    return <BillTrackerAuth onAuthenticated={handleAuthenticated} />;
  }

  return (
    <div className={`bt-wrapper ${theme === 'dark' ? 'theme-dark' : ''}`}>
      <div className="bt-container">
        {/* Top Header matching reference screenshot */}
        <header className="bt-header">
          <div className="bt-header-left">
            <div className="bt-app-icon">
              <FaWallet />
            </div>
            <div className="bt-title-group">
              <h1>
                Bill Payment & Cashflow Tracker
                <span className="bt-version-badge">V2 Pro</span>
              </h1>
              <p className="bt-header-subtitle">
                Liquidity Forecast, 3-Week Tuesday Plan & Multi-Horizon Debt Sizing
              </p>
            </div>
          </div>

          <div className="bt-header-actions">
            {/* Active User Pill */}
            <div className="bt-pill-badge" title={`Private workspace for ${currentUser}`}>
              <FaUser style={{ color: '#4f46e5' }} />
              <span style={{ textTransform: 'capitalize', fontWeight: 700 }}>{currentUser}</span>
            </div>

            {/* Reference Date Pill */}
            <div 
              className="bt-pill-badge"
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
              title={config.autoSyncRealDate !== false ? "Automatically synchronized with today's real calendar date (Click to customize)" : "Manual simulation reference date (Click to customize)"}
              onClick={() => setIsParametersOpen(true)}
            >
              <FaCalendarAlt style={{ color: config.autoSyncRealDate !== false ? '#10b981' : '#64748b' }} />
              <span>{config.autoSyncRealDate !== false ? 'Today' : 'Ref Date'}: {config.referenceDate}</span>
              {config.autoSyncRealDate !== false ? (
                <span style={{
                  fontSize: '0.65rem',
                  backgroundColor: '#d1fae5',
                  color: '#065f46',
                  fontWeight: 700,
                  padding: '1px 5px',
                  borderRadius: '4px',
                  textTransform: 'uppercase'
                }}>
                  Live
                </span>
              ) : (
                <button
                  type="button"
                  style={{
                    fontSize: '0.65rem',
                    backgroundColor: '#ecfdf5',
                    color: '#059669',
                    border: '1px solid #a7f3d0',
                    fontWeight: 700,
                    padding: '2px 6px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfig(prev => ({ ...prev, referenceDate: getTodayDateStr(), autoSyncRealDate: true }));
                  }}
                  title="Return to today's real date"
                >
                  Return to Today
                </button>
              )}
            </div>

            {/* Overdue Pill */}
            <div className={`bt-pill-badge ${overdueSum > 0 ? 'bt-pill-danger' : ''}`}>
              <FaExclamationCircle />
              <span>Overdue: ${overdueSum.toLocaleString(undefined, { minimumFractionDigits: 0 })}</span>
            </div>

            {/* Parameters Button */}
            <button
              className="bt-pill-badge bt-pill-btn"
              onClick={() => setIsParametersOpen(true)}
            >
              <FaSlidersH style={{ color: '#4f46e5' }} />
              <span>Parameters</span>
            </button>

            {/* Lock / Sign Out Button */}
            <button
              className="bt-pill-badge bt-pill-btn"
              onClick={handleLock}
              title="Lock dashboard & sign out"
              style={{ color: '#64748b' }}
            >
              <FaLock />
              <span>Lock</span>
            </button>
          </div>
        </header>

        {/* Multi-Sheet Navigation Tabs */}
        <nav className="bt-tabs-bar">
          <button
            className={`bt-tab-item ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <FaThLarge /> Sheet 1: Dashboard & Cashflow
          </button>

          <button
            className={`bt-tab-item ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <FaChartBar /> Sheet 2: Charts & Visual Analytics
          </button>

          <button
            className={`bt-tab-item ${activeTab === 'expenses' ? 'active' : ''}`}
            onClick={() => setActiveTab('expenses')}
          >
            <FaFileInvoiceDollar /> Expenses & Bills
            <span className="bt-tab-count">{expenses.length}</span>
          </button>

          <button
            className={`bt-tab-item ${activeTab === 'income' ? 'active' : ''}`}
            onClick={() => setActiveTab('income')}
          >
            <FaCoins /> Income Streams
            <span className="bt-tab-count">{incomeStreams.length}</span>
          </button>

          <button
            className={`bt-tab-item ${activeTab === 'credit-card' ? 'active' : ''}`}
            onClick={() => setActiveTab('credit-card')}
          >
            <FaCreditCard /> Credit Card Tracker
            <span className="bt-tab-count" style={{ background: '#ede9fe', color: '#6366f1', fontWeight: 700 }}>
              ${Math.round(creditCard?.balance || 0).toLocaleString()}
            </span>
          </button>
        </nav>

        {/* Welcome banner for brand new users with empty ledger */}
        {expenses.length === 0 && incomeStreams.length === 0 && (
          <div style={{
            backgroundColor: '#eff6ff',
            border: '1px dashed #3b82f6',
            borderRadius: '14px',
            padding: '16px 20px',
            marginBottom: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div>
              <strong style={{ color: '#1d4ed8' }}>Private Workspace for {currentUser}:</strong>
              <span style={{ color: '#1e40af', marginLeft: '6px' }}>
                Your bill tracker is clean and empty. You can add your own income and expenses, or load sample demo data to experiment.
              </span>
            </div>
            <button
              className="bt-btn-secondary"
              style={{ fontSize: '0.82rem', padding: '6px 14px' }}
              onClick={() => {
                setExpenses(sortBillsByDueDate(DEFAULT_EXPENSES));
                setIncomeStreams(DEFAULT_INCOME_STREAMS);
              }}
            >
              Load Sample Template
            </button>
          </div>
        )}

        {/* Tab Content Display */}
        {activeTab === 'dashboard' && (
          <DashboardSheet
            expenses={expenses}
            incomeStreams={incomeStreams}
            config={config}
            setConfig={setConfig}
            onNavigateToTab={(tab) => setActiveTab(tab)}
            onOpenTuesdayPlan={(bill) => setSelectedTuesdayBill(bill)}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsSheet
            expenses={expenses}
            incomeStreams={incomeStreams}
            config={config}
          />
        )}

        {activeTab === 'expenses' && (
          <ExpensesSheet
            expenses={expenses}
            setExpenses={setExpenses}
            config={config}
            setConfig={setConfig}
            onOpenTuesdayPlan={(bill) => setSelectedTuesdayBill(bill)}
          />
        )}

        {activeTab === 'income' && (
          <IncomeSheet
            incomeStreams={incomeStreams}
            setIncomeStreams={setIncomeStreams}
            config={config}
            setConfig={setConfig}
          />
        )}

        {activeTab === 'credit-card' && (
          <CreditCardTracker
            creditCard={creditCard}
            setCreditCard={setCreditCard}
            config={config}
          />
        )}
      </div>

      {/* 3-Week Tuesday Installment Plan Modal */}
      {selectedTuesdayBill && (
        <TuesdayInstallmentModal
          bill={selectedTuesdayBill}
          onClose={() => setSelectedTuesdayBill(null)}
          onToggleInstallmentPaid={handleToggleInstallmentPaid}
        />
      )}

      {/* Parameters & Settings Modal */}
      {isParametersOpen && (
        <ParametersModal
          config={config}
          setConfig={setConfig}
          theme={theme}
          setTheme={setTheme}
          expenses={expenses}
          incomeStreams={incomeStreams}
          creditCard={creditCard}
          setExpenses={setExpenses}
          setIncomeStreams={setIncomeStreams}
          setCreditCard={setCreditCard}
          onClose={() => setIsParametersOpen(false)}
        />
      )}
    </div>
  );
};

export default BillTrackerPage;
